import React, { useState, useEffect, useMemo } from 'react';
import { useNudges, Nudge, AdminBroadcast, LifecycleNotification } from '../../hooks/useNudges';
import { useDismissedNudges } from '../../hooks/useDismissedNudges';
import { getCategoryMeta } from '../../utils/notificationCategories';
import { clickNotification, dismissNotification, dismissLifecycleNotification, nudgeKeyFor } from '../../utils/api';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  TravelExplore as TravelExploreIcon,
  AdminPanelSettings as AdminIcon,
  Close as CloseIcon,
  Sort as SortIcon,
  Security as SecurityIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  Campaign as CampaignIcon,
  NewReleases as NewReleasesIcon,
  FlightTakeoff as FlightTakeoffIcon,
  HeadsetMic as HeadsetMicIcon,
  HomeOutlined as HomeIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import { usePwaInstall } from '../../hooks/usePwaInstall';
import { IOSOverlay } from '../PwaInstallPrompt';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';

// Maps the icon name string from notificationCategories to the actual MUI component
const ICON_BY_NAME: Record<string, React.ComponentType<any>> = {
  Security: SecurityIcon,
  Build: BuildIcon,
  Warning: WarningIcon,
  Campaign: CampaignIcon,
  NewReleases: NewReleasesIcon,
  FlightTakeoff: FlightTakeoffIcon,
};

// Local helper: composes a stable key for a lifecycle nudge (mirrors banner pattern)
const nudgeKey = (n: Nudge) => nudgeKeyFor(n.category, n.tripId);

const severityColor = (severity?: 'info' | 'warning') =>
  severity === 'warning' ? '#ED6C02' : '#0288D1';

// localStorage key for the user's preferred sort order in the bell.
// Values: 'priority' (default per spec §3.3) | 'newest'
const SORT_PREF_KEY = 'bell_sort_pref';
type SortMode = 'priority' | 'newest';

const getInitialSort = (): SortMode => {
  try {
    const v = localStorage.getItem(SORT_PREF_KEY);
    return v === 'newest' ? 'newest' : 'priority';
  } catch {
    return 'priority';
  }
};

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [sortAnchor, setSortAnchor] = useState<null | HTMLElement>(null);
  const [sortMode, setSortMode] = useState<SortMode>(getInitialSort);
  const [userProfile, setUserProfile] = useState<{ displayName?: string; profilePictureUrl?: string; email?: string; role?: string } | null>(null);

  const { canInstall, isIOS, install } = usePwaInstall();
  const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
  const pwaInstallEnabled = !flagsLoading && isEnabled('pwa_install') === true;
  const [showIOSInstall, setShowIOSInstall] = useState(false);

  const {
    nudges,
    admin_broadcasts,
    lifecycle_notifications,
    dismissBroadcast,
    dismissLifecycle,
  } = useNudges();
  const { isDismissed, dismiss } = useDismissedNudges('header_bell');

  // Lifecycle nudges (legacy, local-only) that the user hasn't client-dismissed.
  const visibleNudges = useMemo(
    () => nudges.filter(n => !isDismissed(nudgeKey(n))),
    [nudges, isDismissed]
  );

  // Admin broadcasts in user-chosen sort order.
  // Priority = security → maintenance → site_status → feature_announcement → marketing
  // Newest   = sent_at DESC
  const sortedBroadcasts = useMemo(() => {
    const list = [...admin_broadcasts];
    if (sortMode === 'newest') {
      list.sort((a, b) => (a.sent_at < b.sent_at ? 1 : -1));
    } else {
      list.sort((a, b) => {
        const pa = getCategoryMeta(a.category).priority;
        const pb = getCategoryMeta(b.category).priority;
        if (pa !== pb) return pa - pb;
        // Same category → newest first as tiebreaker
        return a.sent_at < b.sent_at ? 1 : -1;
      });
    }
    return list;
  }, [admin_broadcasts, sortMode]);

  // Lifecycle notifications (Phase 5, server-backed) sorted newest first.
  // No category-priority sort needed yet — only one category exists today.
  const sortedLifecycle = useMemo(() => {
    return [...lifecycle_notifications].sort((a, b) =>
      a.sent_at < b.sent_at ? 1 : -1
    );
  }, [lifecycle_notifications]);

  const totalCount =
    sortedBroadcasts.length + sortedLifecycle.length + visibleNudges.length;

  useEffect(() => {
    // Load user profile
    const loadProfile = async () => {
      const token = localStorage.getItem('idToken');
      if (!token) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/dev'}/users/profile`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setUserProfile(data.profile);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };

    loadProfile();
  }, []);

  // Persist sort preference whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(SORT_PREF_KEY, sortMode);
    } catch {
      // localStorage unavailable (private mode etc.) — silently ignore
    }
  }, [sortMode]);

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  // Click on an admin broadcast row (#211 Phase 3.5).
  // - With deep_link: optimistic dismiss + POST /click + navigate to the link.
  //   The link is the user's destination; the bell row going away is incidental.
  // - Without deep_link: navigate to /notifications/:id where the user can
  //   read the full message and explicitly acknowledge. Don't pre-dismiss
  //   here — the read page handles the click/dismiss POST after the user
  //   actually reads it.
  const handleBroadcastClick = async (b: AdminBroadcast) => {
    setNotificationAnchor(null);

    if (b.deep_link) {
      // Has destination: pre-dismiss (the user is on their way somewhere
      // else and unlikely to re-read this in the bell), record click, navigate.
      dismissBroadcast(b.notification_id);
      try {
        await clickNotification(b.notification_id);
      } catch (err) {
        console.warn('clickNotification failed:', err);
      }
      navigate(b.deep_link);
    } else {
      // No destination: route to the read page. Acknowledgment happens there.
      navigate(`/notifications/${b.notification_id}`);
    }
  };

  // X button on a dismissable broadcast.
  // Server enforces 403 for click-only categories; we hide the X for those,
  // but even if rendered defensively, the server is the source of truth.
  const handleBroadcastDismiss = async (b: AdminBroadcast, e: React.MouseEvent) => {
    e.stopPropagation();
    dismissBroadcast(b.notification_id);
    try {
      await dismissNotification(b.notification_id);
    } catch (err) {
      console.warn('dismissNotification failed:', err);
    }
  };

  // Click on a lifecycle notification (#211 Phase 5).
  // Mirrors the admin broadcast pattern:
  // - With deep_link: optimistic dismiss + POST /click + navigate.
  //   Travel reminders typically deep-link to /trips/:id.
  // - Without deep_link: optimistic dismiss + POST /click. There's no
  //   detail-page read flow for lifecycle notifications today (unlike
  //   admin broadcasts where security messages need full-text reading);
  //   message content is short-form and self-contained in the bell.
  const handleLifecycleClick = async (l: LifecycleNotification) => {
    setNotificationAnchor(null);
    // peek_notification: navigate but do NOT dismiss — user should be able
    // to refer back to it until they explicitly X it out.
    const isPeek = l.category === 'peek_notification';
    if (!isPeek) {
      dismissLifecycle(l.notification_id);
    }
    try {
      await clickNotification(l.notification_id);
    } catch (err) {
      console.warn('clickNotification (lifecycle) failed:', err);
    }
    if (l.deep_link) {
      navigate(l.deep_link);
    }
  };

  // X button on a lifecycle notification.
  // Lifecycle notifications are always X-dismissable (no click-only categories
  // in this source). Backend enforces independently.
  const handleLifecycleDismiss = async (l: LifecycleNotification, e: React.MouseEvent) => {
    e.stopPropagation();
    dismissLifecycle(l.notification_id);
    try {
      await dismissLifecycleNotification(l.notification_id);
    } catch (err) {
      console.warn('dismissLifecycleNotification failed:', err);
    }
  };

  // Lifecycle nudge click — unchanged from previous behavior.
  const handleNudgeClick = (n: Nudge) => {
    setNotificationAnchor(null);
    if (n.category === 'friends') {
      navigate('/friends');
    } else if (n.tripId) {
      navigate(`/trips/${n.tripId}`, { state: { expandNudges: true } });
    }
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setNotificationAnchor(null);
    setProfileAnchor(null);
    setSortAnchor(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    handleClose();
    navigate('/');
  };

  const handleLogoClick = () => {
    navigate('/home');
  };

  // Bell ringing animation — continuous gentle wobble while there are items.
  // MUI sx keyframe; runs only when totalCount > 0 (rotation is null otherwise
  // so the icon sits at rest).
  const bellAnimationSx = totalCount > 0
    ? {
        '@keyframes bellRing': {
          '0%, 70%, 100%': { transform: 'rotate(0deg)' },
          '75%': { transform: 'rotate(8deg)' },
          '80%': { transform: 'rotate(-8deg)' },
          '85%': { transform: 'rotate(5deg)' },
          '90%': { transform: 'rotate(-5deg)' },
          '95%': { transform: 'rotate(2deg)' },
        },
        animation: 'bellRing 3s ease-in-out infinite',
        transformOrigin: 'top center',
      }
    : {};

  // Whether the "Your reminders" section should render at all.
  // Combines server-backed lifecycle notifications and legacy local nudges.
  const remindersHaveContent = sortedLifecycle.length > 0 || visibleNudges.length > 0;

  return (
    <AppBar
      position="fixed"
      elevation={1}
      sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Logo */}
        <Box
          onClick={handleLogoClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
        >
          <TravelExploreIcon
            sx={{
              fontSize: 32,
              color: 'primary.main',
              mr: 1,
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 700,
              color: 'primary.main',
              letterSpacing: '-0.5px',
            }}
          >
            PERCORO
          </Typography>
        </Box>
        {/* Home button — hidden when already on /home (#344) */}
        {location.pathname !== '/home' && (
          <IconButton onClick={() => navigate('/home')} sx={{ color: 'text.secondary' }} aria-label="home" size="small">
            <HomeIcon fontSize="small" />
          </IconButton>
        )}

        {/* Right side icons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Install App — gated behind 'pwa_install' flag; also hidden once installed / on iOS */}
          {canInstall && pwaInstallEnabled && (
            <Tooltip title={isIOS ? "Add to Home Screen" : "Install Percoro App"}>
              <IconButton
                onClick={isIOS ? () => setShowIOSInstall(true) : install}
                sx={{ color: 'text.secondary' }}
                aria-label="install app"
              >
                <GetAppIcon />
              </IconButton>
            </Tooltip>
          )}
{/* Notifications (bell) — hidden on /home (dashboard bell replaces it) and when count is zero (#346) */}
          {totalCount > 0 && location.pathname !== '/home' && (
            <IconButton
              onClick={handleNotificationClick}
              sx={{ color: 'text.secondary' }}
              aria-label="notifications"
            >
              <Badge
                badgeContent={totalCount}
                sx={{ '& .MuiBadge-badge': { bgcolor: '#2D6E6A', color: 'white' } }}
              >
                <NotificationsIcon sx={bellAnimationSx} />
              </Badge>
            </IconButton>
          )}

          {/* Profile (avatar — no badge; notifications live on the bell now) */}
          <IconButton
            onClick={handleProfileClick}
            sx={{ color: 'text.secondary' }}
            aria-label="profile"
          >
            {userProfile?.profilePictureUrl ? (
              <Avatar
                src={userProfile.profilePictureUrl}
                sx={{ width: 32, height: 32 }}
              />
            ) : (
              <AccountCircleIcon />
            )}
          </IconButton>

          {/* Settings */}
          <IconButton
            onClick={() => navigate('/settings')}
            sx={{ color: 'text.secondary' }}
          >
            <SettingsIcon />
          </IconButton>
        </Box>

        {/* Notifications Menu (bell panel) */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleClose}
          PaperProps={{
            sx: { width: 360, maxHeight: 520 },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Header row: title + sort toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.25,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
            {sortedBroadcasts.length > 0 && (
              <Tooltip title={`Sort: ${sortMode === 'priority' ? 'By priority' : 'Newest first'}`}>
                <IconButton
                  size="small"
                  onClick={(e) => setSortAnchor(e.currentTarget)}
                  aria-label="sort announcements"
                >
                  <SortIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Divider />

          {/* Sort menu (only opens when sort icon clicked) */}
          <Menu
            anchorEl={sortAnchor}
            open={Boolean(sortAnchor)}
            onClose={() => setSortAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem
              selected={sortMode === 'priority'}
              onClick={() => { setSortMode('priority'); setSortAnchor(null); }}
            >
              By priority
            </MenuItem>
            <MenuItem
              selected={sortMode === 'newest'}
              onClick={() => { setSortMode('newest'); setSortAnchor(null); }}
            >
              Newest first
            </MenuItem>
          </Menu>

          {/* === Announcements section (admin broadcasts) === */}
          {sortedBroadcasts.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 2,
                  py: 0.75,
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  bgcolor: 'rgba(0, 0, 0, 0.02)',
                }}
              >
                Announcements
              </Typography>
              {sortedBroadcasts.map((b, idx) => {
                const meta = getCategoryMeta(b.category);
                const Icon = ICON_BY_NAME[meta.iconName] ?? CampaignIcon;
                return (
                  <Box key={b.notification_id}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        py: 1,
                        px: 1.5,
                        borderLeft: `3px solid ${meta.color}`,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(0, 180, 160, 0.08)' },
                      }}
                      onClick={() => handleBroadcastClick(b)}
                    >
                      <Icon sx={{ color: meta.color, fontSize: 20, mr: 1, mt: 0.25, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, mr: 0.5, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: meta.color,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            fontSize: '0.65rem',
                            letterSpacing: '0.5px',
                            mb: 0.25,
                          }}
                        >
                          {meta.label}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {b.message}
                        </Typography>
                      </Box>
                      {meta.dismissable && (
                        <IconButton
                          size="small"
                          aria-label="dismiss"
                          onClick={(e) => handleBroadcastDismiss(b, e)}
                          sx={{ flexShrink: 0 }}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      )}
                    </Box>
                    {idx < sortedBroadcasts.length - 1 && <Divider />}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Divider between sections — only if both sections have content */}
          {sortedBroadcasts.length > 0 && remindersHaveContent && <Divider />}

          {/* === Your reminders section (lifecycle notifications + legacy nudges) === */}
          {remindersHaveContent && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 2,
                  py: 0.75,
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  bgcolor: 'rgba(0, 0, 0, 0.02)',
                }}
              >
                Your reminders
              </Typography>

              {/* Lifecycle notifications (Phase 5, server-backed) — render FIRST. */}
              {sortedLifecycle.map((l, idx) => {
                const isLast = idx === sortedLifecycle.length - 1;
                // Show divider between this and the next item (lifecycle or legacy).
                const showDivider = !isLast || visibleNudges.length > 0;
                return (
                  <Box key={l.notification_id}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        py: 1,
                        px: 1.5,
                        borderLeft: `3px solid ${severityColor(l.severity)}`,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(0, 180, 160, 0.08)' },
                      }}
                      onClick={() => handleLifecycleClick(l)}
                    >
                      <FlightTakeoffIcon
                        sx={{
                          color: severityColor(l.severity),
                          fontSize: 20,
                          mr: 1,
                          mt: 0.25,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ flex: 1, mr: 0.5, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {l.message}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        aria-label="dismiss"
                        onClick={(e) => handleLifecycleDismiss(l, e)}
                        sx={{ flexShrink: 0 }}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                    {showDivider && <Divider />}
                  </Box>
                );
              })}

              {/* Legacy nudges (client-side dismissed) — render BELOW lifecycle. */}
              {visibleNudges.map((n, idx) => {
                const key = nudgeKey(n);
                return (
                  <Box key={key}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 0.75,
                        px: 1.5,
                        borderLeft: `3px solid ${severityColor(n.severity)}`,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(0, 180, 160, 0.08)' },
                      }}
                      onClick={() => handleNudgeClick(n)}
                    >
                      <Typography variant="body2" sx={{ flex: 1, mr: 1, whiteSpace: 'normal' }}>
                        {n.tripTitle ? (
                          <><strong>{n.tripTitle}</strong> &mdash; {n.message}</>
                        ) : (
                          n.message
                        )}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="dismiss"
                        onClick={(e) => {
                          e.stopPropagation();
                          dismiss(key);
                        }}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                    {idx < visibleNudges.length - 1 && <Divider />}
                  </Box>
                );
              })}
            </Box>
          )}
        </Menu>

        {/* Profile Menu */}
        <Menu
          anchorEl={profileAnchor}
          open={Boolean(profileAnchor)}
          onClose={handleClose}
          PaperProps={{
            sx: { width: 200 },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* User identity block */}
          {(userProfile?.displayName || userProfile?.email) && (
            <Box sx={{ px: 2, py: 1.5 }}>
              {userProfile?.displayName && (
                <Typography variant="subtitle2" fontWeight={600} noWrap>
                  {userProfile.displayName}
                </Typography>
              )}
              {userProfile?.email && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {userProfile.email}
                </Typography>
              )}
            </Box>
          )}
          {(userProfile?.displayName || userProfile?.email) && <Divider />}
          <MenuItem onClick={() => { handleClose(); navigate('/settings'); }}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>My Profile</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => { handleClose(); navigate('/settings', { state: { openSection: 'support' } }); }}>
            <ListItemIcon>
              <HeadsetMicIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Contact Support</ListItemText>
          </MenuItem>
          {(userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
            <MenuItem onClick={() => { handleClose(); navigate('/admin'); }}>
              <ListItemIcon>
                <AdminIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Admin Dashboard</ListItemText>
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
        {showIOSInstall && <IOSOverlay onDismiss={() => setShowIOSInstall(false)} />}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
